import React, { useState, useEffect } from 'react';
import { Select, Input, Spin } from 'antd';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import ConfirmModal from 'src/components/confirm-modal';
import RollbackItem from 'src/components/rollback-item';
import { getReasons } from 'src/apis/fitReason';
import './style.scss';

const { TextArea } = Input;

function ConfirmRollbackModal({
    onCancel,
    onConfirm,
    deallocations,
    matchData,
    needs,
    loading,
}) {
    const matchPeople = deallocations.filter(
        (dealloc) => dealloc.id === matchData.deallocationId
    );
    const need = needs.find(n => n.id === matchData.matchNeedId);

    const { t, i18n } = useTranslation();
    const isEn = i18n.language === 'en' || i18n.language?.startsWith('en');

    const [options, setOptions] = useState([]);
    const [fetchingReasons, setFetchingReasons] = useState(true);
    const [selectedValue, setSelectedValue] = useState(null);
    const [selectedMeta, setSelectedMeta] = useState(null);
    const [otherText, setOtherText] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        getReasons('ROLLBACK')
            .then(response => {
                const categories = response?.message || [];
                const opts = categories.flatMap(category =>
                    category.subcategories.map(sub => ({
                        value: sub.subcategoryKey ?? 'OTHERS',
                        label: isEn ? sub.subcategoryEn : sub.subcategoryPtBr,
                        categoryKey: category.categoryKey,
                        isOther: sub.isOther,
                    }))
                );
                setOptions(opts);
            })
            .catch(() => setOptions([]))
            .finally(() => setFetchingReasons(false));
    }, [isEn]);

    const handleSelect = (value) => {
        const meta = options.find(o => o.value === value);
        setSelectedValue(value);
        setSelectedMeta(meta);
        setOtherText('');
        setError(null);
    };

    const handleConfirm = () => {
        if (!selectedMeta) {
            setError(t('undoFitReasonRequired'));
            return;
        }
        if (selectedMeta.isOther && otherText.trim().length < 3) {
            setError(t('undoFitOtherReasonRequired'));
            return;
        }
        const rollbackSubcategory = selectedMeta.isOther ? otherText.trim() : selectedValue;
        onConfirm(
            matchData.deallocationId,
            matchData.matchNeedId,
            selectedMeta.isOther ? otherText.trim() : null,
            selectedMeta.categoryKey,
            rollbackSubcategory
        );
    };

    return (
        <div>
            <ConfirmModal
                onCancel={onCancel}
                onConfirm={handleConfirm}
                okText={t('confirm')}
                cancelText={t('cancel')}
                title={t('confirmRollback')}
                visible={true}
                className='rollback-opportunity'
                confirmLoading={loading}
            >
                <div className='rollback-opportunity__container'>
                    <p className='rollback-opportunity__container_subtitle'>
                        {t('confirmRollbackSubtitle')}
                    </p>
                    <RollbackItem matchPeople={matchPeople} need={need} type="need" />

                    <div className='rollback-opportunity__field'>
                        <label className='rollback-opportunity__label'>
                            {t('selectTheReason')}*
                        </label>
                        {fetchingReasons ? (
                            <Spin />
                        ) : (
                            <Select
                                style={{ width: '100%' }}
                                options={options}
                                value={selectedValue}
                                onChange={handleSelect}
                                placeholder={t('selectTheReason')}
                            />
                        )}
                    </div>

                    {selectedMeta?.isOther && (
                        <div className='rollback-opportunity__field'>
                            <label className='rollback-opportunity__label'>
                                {t('otherReasonLabel')}*
                            </label>
                            <TextArea
                                maxLength={50}
                                showCount
                                rows={4}
                                value={otherText}
                                onChange={e => { setOtherText(e.target.value); setError(null); }}
                                placeholder={t('rollbackOtherReasonPlaceholder')}
                            />
                        </div>
                    )}

                    {error && (
                        <p className='rollback-opportunity__error' role='alert'>{error}</p>
                    )}
                </div>
            </ConfirmModal>
        </div>
    );
}

ConfirmRollbackModal.defaultProps = {
    onCancel: noop,
    onConfirm: noop,
    deallocations: [],
    matchData: {matchNeedId: '', deallocationId: ''},
    needs: [],
    loading: false,
};

export default ConfirmRollbackModal;
